"use client";

import PageBreadCrumb from "@/components/common/PageBreadCrumb";
import Button from "@/components/ui/button/Button";
import Input from "@/components/form/input/InputField";
import Label from "@/components/form/Label";
import { Modal } from "@/components/ui/modal/index";
import { rmcClient } from "@/lib/rmcClient";
import { handleApiError } from "@/lib/api";
import { useAppStore } from "@/lib/store";
import { validateGst } from "@/lib/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import toast from "react-hot-toast";
import DataTable from "@/components/ui/DataTable";
import Badge from "@/components/ui/badge/Badge";
import { PencilIcon, TrashBinIcon } from "@/icons";

export default function RmcCompany() {
  const qc = useQueryClient();
  const setActiveFY = useAppStore((s) => s.setActiveFY);
  const [gstHint, setGstHint] = React.useState(null);
  const [confirmFy, setConfirmFy] = React.useState(null);
  const [createFyOpen, setCreateFyOpen] = React.useState(false);
  const [editFy, setEditFy] = React.useState(null);
  const [deleteFy, setDeleteFy] = React.useState(null);
  const [createFyForm, setCreateFyForm] = React.useState({
    label: "",
    fy_suffix: "",
    start_date: "",
    end_date: "",
  });
  const [editFyForm, setEditFyForm] = React.useState({
    label: "",
    fy_suffix: "",
    start_date: "",
    end_date: "",
  });
  const [dirty, setDirty] = React.useState(false);
  const [form, setForm] = React.useState({});

  const { data: company, isLoading } = useQuery({
    queryKey: ["company"],
    queryFn: async () => (await rmcClient.getCompany()).data,
  });

  const { data: fyList = [] } = useQuery({
    queryKey: ["financialYears"],
    queryFn: async () => (await rmcClient.listFinancialYears()).data,
  });

  React.useEffect(() => {
    if (company) setForm(company);
  }, [company]);

  const saveCo = useMutation({
    mutationFn: (p) => rmcClient.putCompany(p),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["company"] });
      setDirty(false);
      toast.success("Company saved");
    },
  });

  const act = useMutation({
    mutationFn: (id) => rmcClient.activateFinancialYear(id),
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ["financialYears"] });
      qc.invalidateQueries({ queryKey: ["financial-years", "active"] });
      const a = (await rmcClient.getActiveFY()).data;
      setActiveFY(a);
      setConfirmFy(null);
      toast.success("Financial year activated");
    },
    onError: (err) => {
      setConfirmFy(null);
      toast.error(handleApiError(err));
    },
  });

  const createFy = useMutation({
    mutationFn: (payload) => rmcClient.createFinancialYear(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["financialYears"] });
      setCreateFyOpen(false);
      setCreateFyForm({
        label: "",
        fy_suffix: "",
        start_date: "",
        end_date: "",
      });
      toast.success("Financial year created");
    },
    onError: (err) => {
      setCreateFyOpen(false);
      toast.error(handleApiError(err));
    },
  });

  const updateFy = useMutation({
    mutationFn: ({ id, payload }) => rmcClient.updateFinancialYear(id, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["financialYears"] });
      setEditFy(null);
      toast.success("Financial year updated");
    },
    onError: (err) => {
      setEditFy(null);
      toast.error(handleApiError(err));
    },
  });

  const removeFy = useMutation({
    mutationFn: (id) => rmcClient.deleteFinancialYear(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["financialYears"] });
      setDeleteFy(null);
      toast.success("Financial year deleted");
    },
    onError: (err) => {
      setDeleteFy(null);
      toast.error(handleApiError(err));
    },
  });

  const gstStatus = validateGst(form.gst_number || "");

  return (
    <div className="space-y-6">
      <PageBreadCrumb pageTitle="Company & Financial Year" />
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <div className="rounded-2xl border border-gray-200/90 dark:border-gray-800 bg-white dark:bg-gray-900/60 p-6 sm:p-7 space-y-5 shadow-theme-sm">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Company Profile</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Maintain legal and contact details used across invoices and reports.
            </p>
          </div>
          {isLoading ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : (
            <>
              <div>
                <Label>Name</Label>
                <Input
                  value={form.name || ""}
                  onChange={(e) => {
                    setForm({ ...form, name: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Address</Label>
                <Input
                  value={form.address || ""}
                  onChange={(e) => {
                    setForm({ ...form, address: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Phone</Label>
                <Input
                  value={form.contact_phone || ""}
                  onChange={(e) => {
                    setForm({ ...form, contact_phone: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Contact Email</Label>
                <Input
                  value={form.contact_email || ""}
                  onChange={(e) => {
                    setForm({ ...form, contact_email: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>GST Number</Label>
                <Input
                  value={form.gst_number || ""}
                  onChange={(e) => {
                    const v = e.target.value.toUpperCase();
                    setForm({ ...form, gst_number: v });
                    setDirty(true);
                    const t = validateGst(v);
                    if (t.empty) setGstHint(null);
                    else setGstHint(t.ok ? "ok" : "err");
                  }}
                  className="mt-1"
                />
                {gstHint === "ok" && (
                  <p className="mt-1 text-[11px] text-green-400">✓ Valid GST format</p>
                )}
                {gstHint === "err" && !gstStatus.empty && (
                  <p className="mt-1 text-[11px] text-red-400">Invalid GST format</p>
                )}
              </div>
            </>
          )}
        </div>

        <div className="rounded-2xl border border-gray-200/90 dark:border-gray-800 bg-white dark:bg-gray-900/60 p-6 sm:p-7 space-y-5 shadow-theme-sm">
          <div className="space-y-1">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Bank & Footer Details</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Set invoice blessing line and company bank account information.
            </p>
          </div>
          {isLoading ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : (
            <>
              <div>
                <Label>Blessing</Label>
                <Input
                  value={form.blessing || ""}
                  onChange={(e) => {
                    setForm({ ...form, blessing: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Bank Name</Label>
                <Input
                  value={form.bank_name || ""}
                  onChange={(e) => {
                    setForm({ ...form, bank_name: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Bank Account No</Label>
                <Input
                  value={form.bank_account_no || ""}
                  onChange={(e) => {
                    setForm({ ...form, bank_account_no: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Bank IFSC Code</Label>
                <Input
                  value={form.bank_ifsc_code || ""}
                  onChange={(e) => {
                    setForm({ ...form, bank_ifsc_code: e.target.value.toUpperCase() });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Bank Branch</Label>
                <Input
                  value={form.bank_branch || ""}
                  onChange={(e) => {
                    setForm({ ...form, bank_branch: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Account Type</Label>
                <Input
                  value={form.account_type || ""}
                  onChange={(e) => {
                    setForm({ ...form, account_type: e.target.value });
                    setDirty(true);
                  }}
                  className="mt-1"
                />
              </div>
            </>
          )}
        </div>
      </div>
      <div>
        <Button size="sm" disabled={!dirty || gstHint === "err"} onClick={() => saveCo.mutate(form)}>
          Save Changes
        </Button>
      </div>

      <div>
        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white">Financial Years</h3>
          <Button
            size="sm"
            onClick={() => setCreateFyOpen(true)}
          >
            Add FY
          </Button>
        </div>
        <DataTable
          columns={[
            { key: "label", header: "FY" },
            {
              key: "active",
              header: "Status",
              render: (r) =>
                r.is_active ? (
                  <Badge color="success">Active</Badge>
                ) : (
                  <span className="text-gray-500">—</span>
                ),
            },
            {
              key: "act",
              header: "",
              render: (r) =>
                !r.is_active ? (
                  <div className="flex w-full items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={act.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmFy(r);
                      }}
                    >
                      Activate
                    </Button>
                    <button
                      type="button"
                      aria-label="Edit financial year"
                      title="Edit"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-white/5 dark:hover:text-white"
                      disabled={updateFy.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditFy(r);
                        setEditFyForm({
                          label: r.label || "",
                          fy_suffix: r.fy_suffix || "",
                          start_date: r.start_date || "",
                          end_date: r.end_date || "",
                        });
                      }}
                    >
                      <PencilIcon className="size-4" />
                    </button>
                    <button
                      type="button"
                      aria-label="Delete financial year"
                      title="Delete"
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-red-50 hover:text-red-600 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                      disabled={removeFy.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteFy(r);
                      }}
                    >
                      <TrashBinIcon className="size-4" />
                    </button>
                  </div>
                ) : null,
            },
          ]}
          data={fyList}
          isLoading={false}
        />
      </div>

      <Modal
        isOpen={!!confirmFy}
        onClose={() => setConfirmFy(null)}
        className="max-w-md"
        showCloseButton={false}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Switching to FY {confirmFy?.label} will start new document sequences.
            Existing records will not be affected.
          </p>
          <div className="flex justify-end gap-2">
            <Button size="sm" onClick={() => act.mutate(confirmFy.id)}>
              Confirm
            </Button>
            <Button size="sm" variant="outline" onClick={() => setConfirmFy(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={createFyOpen}
        onClose={() => setCreateFyOpen(false)}
        className="max-w-xl"
      >
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Add Financial Year</h4>
          <div>
            <Label>Label</Label>
            <Input
              value={createFyForm.label}
              onChange={(e) => setCreateFyForm((s) => ({ ...s, label: e.target.value }))}
            />
          </div>
          <div>
            <Label>FY Suffix</Label>
            <Input
              value={createFyForm.fy_suffix}
              onChange={(e) => setCreateFyForm((s) => ({ ...s, fy_suffix: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={createFyForm.start_date}
                onChange={(e) => setCreateFyForm((s) => ({ ...s, start_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={createFyForm.end_date}
                onChange={(e) => setCreateFyForm((s) => ({ ...s, end_date: e.target.value }))}
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button
              size="sm"
              disabled={createFy.isPending}
              onClick={() => createFy.mutate(createFyForm)}
            >
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setCreateFyOpen(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!editFy}
        onClose={() => setEditFy(null)}
        className="max-w-xl"
      >
        <div className="space-y-3">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Edit Financial Year</h4>
          <div>
            <Label>Label</Label>
            <Input
              value={editFyForm.label}
              onChange={(e) => setEditFyForm((s) => ({ ...s, label: e.target.value }))}
            />
          </div>
          <div>
            <Label>FY Suffix</Label>
            <Input
              value={editFyForm.fy_suffix}
              onChange={(e) => setEditFyForm((s) => ({ ...s, fy_suffix: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={editFyForm.start_date}
                onChange={(e) => setEditFyForm((s) => ({ ...s, start_date: e.target.value }))}
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={editFyForm.end_date}
                onChange={(e) => setEditFyForm((s) => ({ ...s, end_date: e.target.value }))}
              />
            </div>
          </div>
          <div className="mt-2 flex justify-end gap-2">
            <Button
              size="sm"
              disabled={updateFy.isPending}
              onClick={() =>
                updateFy.mutate({
                  id: editFy.id,
                  payload: editFyForm,
                })
              }
            >
              Save
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditFy(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!deleteFy}
        onClose={() => setDeleteFy(null)}
        className="max-w-md"
        showCloseButton={false}
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Delete financial year <span className="font-semibold">{deleteFy?.label}</span>?
          </p>
          <div className="flex justify-end gap-2">
            <Button size="sm" disabled={removeFy.isPending} onClick={() => removeFy.mutate(deleteFy.id)}>
              Delete
            </Button>
            <Button size="sm" variant="outline" onClick={() => setDeleteFy(null)}>
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
